import { PrimaryButton, SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body3, H2 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { routes } from '@/modules/Router';
import { useChangePassword, useGenerateResetToken } from '@/modules/Users/Users.api';
import { changePasswordFormInputSchema, ChangePasswordFormInputSchema } from '@/modules/Users/Users.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export function ChangePasswordForm() {
  const { register, handleSubmit, formState } = useForm<ChangePasswordFormInputSchema>({
    mode: 'onBlur',
    resolver: zodResolver(changePasswordFormInputSchema),
  });

  const mutationGenerateResetToken = useGenerateResetToken();
  const mutationChangePassword = useChangePassword();
  const navigate = useNavigate();

  const onSubmit = (data: ChangePasswordFormInputSchema) => {
    mutationGenerateResetToken.mutate(
      {
        reset_type: 'PASSWORD',
        password: data.oldPassword,
      },
      {
        onSuccess: (responseData) => {
          const token = responseData.token;
          mutationChangePassword.mutate(
            {
              token: token,
              reset_type: 'PASSWORD',
              new_password: data.newPassword,
            },
            {
              onSuccess: () => {
                toast.success('Updated your password!', { autoClose: 5000 });
                navigate(routes.login);
              },

              onError: () => {
                toast.error('Failed to update the password. Please try again later.', {
                  autoClose: 3000,
                });
                console.error('Submitting Update Password failed. Error message:', mutationChangePassword.error);
              },
            }
          );
        },
        onError: (error) => {
          toast.error(error.message || 'Could not change password.', {
            autoClose: 3000,
          });
        },
      }
    );
  };

  const onCancel = () => {
    navigate(-1);
  };

  return (
    <div className={cn('flex flex-grow justify-center bg-secondary-15 items-center p-16 place-items-center mb-8')}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col w-[400px] space-y-8')}>
        <div className={cn('flex flex-col')}>
          <H2 className={cn('text-lg font-semibold text-gray-700')}>Change Password</H2>
          <Body3 className={cn('text-neutral-500')}>
            Please enter your old password, for security&apos;s sake, and then enter your new password twice so we can verify you typed it
            in correctly.
          </Body3>
        </div>
        {/* old password input field */}
        <div>
          <label htmlFor="old_password" className={cn('block text-sm font-medium text-gray-700')}>
            Old Password
          </label>
          <input
            {...register('oldPassword')}
            type="password"
            id="old_password"
            className={cn(
              'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            )}
          />
          <div className={cn('h-4')}>
            {formState.errors.oldPassword && (
              <div className={cn('text-error-500 bg-error-50 border-error-200 rounded px-2 py-1 mt-0 mx-2 text-sm')}>
                {formState.errors.oldPassword?.message}
              </div>
            )}
          </div>
        </div>

        {/* new password input field */}
        <div>
          <label htmlFor="new_password" className={cn('block text-sm font-medium text-gray-700')}>
            New Password
          </label>
          <input
            {...register('newPassword')}
            type="password"
            id="new_password"
            className={cn(
              'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            )}
          />
          <div className={cn('h-4')}>
            {formState.errors.newPassword && (
              <div className={cn('text-error-500 bg-error-50 border-error-200 rounded px-2 py-1 mt-0 mx-2 text-sm')}>
                {formState.errors.newPassword?.message}
              </div>
            )}
          </div>
        </div>

        {/* confirm password input field */}
        <div>
          <label htmlFor="confirm_password" className={cn('block text-sm font-medium text-gray-700')}>
            New Password
          </label>
          <input
            {...register('confirmNewPassword')}
            type="password"
            id="confirm_password"
            className={cn(
              'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            )}
          />
          <div className={cn('h-4')}>
            {formState.errors.confirmNewPassword && (
              <div className={cn('text-error-500 bg-error-50 border-error-200 rounded px-2 py-1 mt-0 mx-2 text-sm')}>
                {formState.errors.confirmNewPassword?.message}
              </div>
            )}
          </div>
        </div>

        <div className={cn('flex gap-3 justify-end')}>
          <SecondaryButton
            onClick={onCancel}
            disabled={mutationChangePassword.status === 'pending' || mutationGenerateResetToken.status === 'pending'}
            className="min-w-[80px]"
            variant="stretch"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={mutationChangePassword.status === 'pending' || mutationGenerateResetToken.status === 'pending'}
            className="min-w-[120px] whitespace-nowrap"
            variant="stretch"
          >
            {mutationChangePassword.status === 'pending' || mutationGenerateResetToken.status === 'pending' ? (
              <div className={cn('w-full flex justify-center items-center')}>
                <Loading size={'small'} inline />
              </div>
            ) : (
              'Change Password'
            )}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
